/**
 * linesDisplay.js — pure display-layer helpers for the Lines tab (#63 follow-up,
 * client-feedback round PLAN-lines-feedback-round).
 *
 * v3 ledger identity strings (fund_name / investor) are normalized SLUGS —
 * lowercase, punctuation folded away. Filtering/search/sort continue to key
 * on those RAW slugs (see ledgerFilters.js), now via foldTight EQUALITY for
 * fund/investor — nothing here changes that matching behavior, it only
 * consumes it (buildLinesFacets). This module produces the strings a client
 * SEES: real display names in Title Case, patterned document names, members
 * grouped, faceted dropdown option lists, and a CSV export built from the
 * same slug-keyed facts. Every function here is pure — no React, no fetch,
 * no mutation of its inputs.
 */
import {
  foldForMatch,
  foldTight,
  foldGroupMemberNames,
  investorMatchesFoldedNames,
  applyLinesFilters,
} from "./ledgerFilters";

/**
 * buildFundDisplayMap(portals) -> { [foldTight(fund_name)]: displayString }
 *
 * portals: the data.portals array from /api/ledger/overview
 * ({portal, funds:[{fund_name, fund_display_name?, ...}]}).
 *
 * Best display string per fund = fund_display_name when present, else the
 * fund_name slug itself. On a foldTight key collision (two funds whose
 * names fold to the same tight key), prefer the entry that HAS a
 * fund_display_name over one that doesn't; if that's still tied, prefer the
 * longest string.
 */
export function buildFundDisplayMap(portals) {
  const map = {};
  const hasDisplayName = {}; // parallel tracking: did the winning entry come from fund_display_name?

  for (const portal of portals || []) {
    for (const fund of (portal && portal.funds) || []) {
      const key = foldTight(fund.fund_name);
      if (!key) continue;
      const hasDisplay = !!fund.fund_display_name;
      const candidate = fund.fund_display_name || fund.fund_name;

      if (!(key in map)) {
        map[key] = candidate;
        hasDisplayName[key] = hasDisplay;
        continue;
      }

      const existingHasDisplay = hasDisplayName[key];
      if (hasDisplay && !existingHasDisplay) {
        // Candidate with a real display name beats one without, regardless of length.
        map[key] = candidate;
        hasDisplayName[key] = true;
      } else if (hasDisplay === existingHasDisplay && candidate.length > map[key].length) {
        // Same tier (both have / both lack a display name) — longest string wins.
        map[key] = candidate;
      }
    }
  }

  return map;
}

/**
 * buildMemberDisplayMap(lines, groups) -> { [foldTight(investor)]: displayString }
 *
 * lines: flat /api/ledger/lines feed (each carries a slug `investor`).
 * groups: [{group_id, name, members:[rawDisplayName,...]}] from /api/groups.
 *
 * For every distinct investor slug seen in lines, look across ALL groups'
 * raw member names for ones that bidirectionally dual-fold match it (same
 * semantics as the group FILTER, investorMatchesFoldedNames) — several
 * matches -> the longest raw name wins (most likely the fully-punctuated
 * form). No match -> the investor slug is used as-is (nothing better to show).
 */
export function buildMemberDisplayMap(lines, groups) {
  // Precompute the fold of every raw group member name once, keeping the raw
  // string alongside so a match can report it back.
  const foldedMembers = [];
  for (const group of groups || []) {
    for (const raw of (group && group.members) || []) {
      const [folded] = foldGroupMemberNames({ members: [raw] });
      if (folded) foldedMembers.push({ raw, folded });
    }
  }

  const map = {};
  for (const line of lines || []) {
    const investor = line && line.investor;
    const key = foldTight(investor);
    if (!key) continue; // guard on the FOLDED key (not the raw string) — a punctuation-only
    // investor like a lone Hebrew geresh is truthy but folds to "", and writing that into
    // map[""] would corrupt displayName("") for every genuinely-blank-investor line.
    if (key in map) continue; // already resolved this identity

    let best = null;
    for (const { raw, folded } of foldedMembers) {
      if (investorMatchesFoldedNames(investor, [folded])) {
        if (!best || raw.length > best.length) best = raw;
      }
    }
    map[key] = best || investor;
  }

  return map;
}

// ACRONYMS: dot-strippable tokens whose letters get fully uppercased by
// titleCaseDisplay ("l.p." -> "L.P.", "lp" -> "LP"), founder-approved list.
const ACRONYMS = new Set([
  "lp", "llc", "llp", "gp", "spv", "ic", "clo", "reit", "scsp", "fbo", "plc", "bv", "sa", "ag", "jt", "jtwros",
]);

// Roman numeral tokens (i..xiii) that titleCaseDisplay uppercases whole,
// same as an acronym: "ii" -> "II".
const ROMAN_NUMERAL_TOKENS = new Set([
  "i", "ii", "iii", "iv", "v", "vi", "vii", "viii", "ix", "x", "xi", "xii", "xiii",
]);

/**
 * titleCaseDisplay(s) -> Title Case display string.
 *
 * "" for falsy input.
 *
 * A CURATED mixed-case string — one that already contains BOTH an uppercase
 * and a lowercase Latin letter (e.g. a group's raw member name "Emrald for
 * Good Ltd.", or a fund_display_name someone typed with real casing) — is
 * assumed to already be the correct display form and is returned VERBATIM,
 * untouched.
 *
 * A UNIFORM-case string (a lowercase slug like "argis living fund ii lp", or
 * an ALL-CAPS raw value like "EMRALD FOR GOOD LTD" — nothing worth
 * preserving either way) is recased word-by-word: split on whitespace,
 * punctuation stays attached to its token.
 *   - a token whose letters (all non-letter characters stripped, not just
 *     dots) case-insensitively match ACRONYMS has its letters uppercased,
 *     punctuation untouched: "l.p." -> "L.P.", "lp" -> "LP", "spv" -> "SPV",
 *     "l.p.," -> "L.P.," (trailing comma after a legal suffix).
 *   - a roman-numeral token (i..xiii) is uppercased whole: "ii" -> "II",
 *     "ii," -> "II,".
 *   - anything else gets First-letter-upper, rest-lower: "ltd" -> "Ltd".
 *
 * Non-Latin scripts (Hebrew) have no case — toUpperCase()/toLowerCase() are
 * no-ops on them — so they pass through untouched by either branch.
 */
export function titleCaseDisplay(s) {
  if (!s) return "";
  const str = String(s);

  const hasUpper = /[A-Z]/.test(str);
  const hasLower = /[a-z]/.test(str);
  if (hasUpper && hasLower) return str; // curated mixed-case name — already correct, verbatim

  return str
    .trim()
    .split(/\s+/)
    .map((token) => {
      // Strip ALL non-letter characters (not just dots) before matching — a
      // token like "II," or "L.P.," (roman numeral / acronym immediately
      // followed by a comma, e.g. the common "<Name> II, L.P." legal-suffix
      // pattern) must still match ACRONYMS/ROMAN_NUMERAL_TOKENS; only the
      // dot-stripped letters were checked before, so trailing punctuation
      // other than a dot fell through to the generic recase fallback below.
      const bareLetters = token.toLowerCase().replace(/[^a-z]/g, "");
      if (ACRONYMS.has(bareLetters) || ROMAN_NUMERAL_TOKENS.has(bareLetters)) {
        return token.toUpperCase();
      }
      return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
    })
    .join(" ");
}

/**
 * displayName(raw, map) -> Title Case display string (see titleCaseDisplay).
 * map is keyed by foldTight(raw) (buildFundDisplayMap / buildMemberDisplayMap
 * output). Falls back to the raw slug, then "".
 */
export function displayName(raw, map = {}) {
  return titleCaseDisplay((map && map[foldTight(raw)]) || raw || "");
}

// ── buildFuzzyCanon (fuzzy-match canonicalization, client-feedback round) ───
//
// tokens(l) for the merge decision below (word-order/punctuation/dash-style
// insensitive — foldForMatch already folds all of those to spaces).
function fuzzyTokens(label) {
  return new Set(foldForMatch(label).split(/\s+/).filter(Boolean));
}

// A DISTINGUISHER token in the symmetric difference between two labels'
// token sets blocks a fuzzy (Dice) merge even when Dice clears the 0.9 bar —
// it's the signal that two otherwise near-identical labels name DIFFERENT
// things ("SPV 49" vs "SPV 50", "Fund II" vs "Fund III", a single first-name
// token). Digits, roman numerals (i..xiii), and single characters all count.
function isDistinguisherToken(token) {
  return /\d/.test(token) || ROMAN_NUMERAL_TOKENS.has(token) || token.length === 1;
}

function diceScore(a, b) {
  let intersection = 0;
  for (const t of a) if (b.has(t)) intersection += 1;
  return (2 * intersection) / (a.size + b.size);
}

function tokenSetsEqual(a, b) {
  if (a.size !== b.size) return false;
  for (const t of a) if (!b.has(t)) return false;
  return true;
}

// Two labels merge when their token sets are IDENTICAL (word order/
// punctuation/dash-style variance only — always safe), or when Dice >= 0.9
// AND no token unique to either side is a DISTINGUISHER. Labels that fold to
// zero tokens (punctuation-only) never merge with anything — same
// no-meaningful-identity guard uniqueFoldedEntries applies elsewhere in this
// module — so several unrelated blank-ish labels can never collapse into one
// arbitrary canonical.
function shouldMergeLabels(tokensA, tokensB) {
  if (tokensA.size === 0 || tokensB.size === 0) return false;
  if (tokenSetsEqual(tokensA, tokensB)) return true;
  if (diceScore(tokensA, tokensB) < 0.9) return false;
  for (const t of tokensA) if (!tokensB.has(t) && isDistinguisherToken(t)) return false;
  for (const t of tokensB) if (!tokensA.has(t) && isDistinguisherToken(t)) return false;
  return true;
}

/**
 * buildFuzzyCanon(values, map) -> (raw) => canonicalLabel
 *
 * Canonicalizes a set of raw fund/investor identity strings ONCE, at the
 * source, so every downstream consumer (dropdowns, filters, grouping, CSV)
 * reads already-de-duplicated values with zero changes to their own
 * fold-equality logic.
 *
 * values: array of raw strings (dupes/falsy ignored). map: a foldTight-keyed
 * display map (buildFundDisplayMap / buildMemberDisplayMap output), or {}.
 *
 * Step 1 (label-resolve): label(raw) = map[foldTight(raw)] || raw — the same
 * resolution displayName uses, before Title Case. Distinct raw hash slugs
 * that all resolve to the SAME map entry collapse to one label here for
 * free, no clustering needed.
 *
 * Step 2 (cluster): the DISTINCT labels are unioned via union-find —
 * shouldMergeLabels (above) decides each pair; see its doc for the merge
 * rule and the distinguisher guard. NEVER lower the 0.9 Dice threshold.
 *
 * Step 3 (canonical label): per cluster, prefer a label that came from a map
 * hit (on ANY raw that produced it); tie-break longest, then lexicographic
 * (determinism).
 *
 * Returns a pure lookup function: every raw value seen in `values` resolves
 * from a prebuilt table; an unseen raw at call time is label-resolved fresh
 * and matched against an existing cluster by EXACT token-set equality only
 * (no fuzzy Dice check for unseen input), falling back to its own label if
 * nothing matches. Falsy raw input passes through unchanged. Pure, no
 * mutation of `values`/`map`.
 */
export function buildFuzzyCanon(values, map) {
  const m = map || {};

  // Step 1: resolve every distinct raw value to its label, tracking whether
  // ANY occurrence of a given label came from a real map hit.
  const rawToLabel = new Map();
  const labelTokens = new Map(); // label -> Set of tokens
  const labelFromMap = new Map(); // label -> boolean

  for (const raw of values || []) {
    if (!raw || rawToLabel.has(raw)) continue;
    const mapped = m[foldTight(raw)];
    const label = mapped || raw;
    rawToLabel.set(raw, label);
    if (!labelTokens.has(label)) labelTokens.set(label, fuzzyTokens(label));
    labelFromMap.set(label, !!labelFromMap.get(label) || !!mapped);
  }

  const labels = [...labelTokens.keys()];

  // Step 2: cluster distinct labels via union-find (O(distinct^2) pairwise —
  // fine at this scale, ~130 labels).
  const parent = new Map(labels.map((l) => [l, l]));
  const find = (x) => {
    while (parent.get(x) !== x) {
      parent.set(x, parent.get(parent.get(x)));
      x = parent.get(x);
    }
    return x;
  };
  const union = (a, b) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };

  for (let i = 0; i < labels.length; i++) {
    for (let j = i + 1; j < labels.length; j++) {
      if (shouldMergeLabels(labelTokens.get(labels[i]), labelTokens.get(labels[j]))) {
        union(labels[i], labels[j]);
      }
    }
  }

  // Step 3: canonical label per cluster.
  const clusterMembers = new Map(); // root -> [labels] (encounter order)
  for (const l of labels) {
    const root = find(l);
    if (!clusterMembers.has(root)) clusterMembers.set(root, []);
    clusterMembers.get(root).push(l);
  }

  const rootCanonical = new Map();
  for (const [root, members] of clusterMembers) {
    let best = members[0];
    for (const l of members.slice(1)) {
      const bestFromMap = labelFromMap.get(best);
      const lFromMap = labelFromMap.get(l);
      if (lFromMap !== bestFromMap) {
        if (lFromMap) best = l;
        continue;
      }
      if (l.length !== best.length) {
        if (l.length > best.length) best = l;
        continue;
      }
      if (l < best) best = l;
    }
    rootCanonical.set(root, best);
  }

  const labelCanonical = new Map(labels.map((l) => [l, rootCanonical.get(find(l))]));
  const rawCanonical = new Map();
  for (const [raw, label] of rawToLabel) {
    rawCanonical.set(raw, labelCanonical.get(label));
  }

  return function canonicalize(raw) {
    if (!raw) return raw;
    if (rawCanonical.has(raw)) return rawCanonical.get(raw);

    // Unseen raw: resolve its label fresh, match an existing cluster by
    // EXACT token-set equality only (no fuzzy Dice check for unseen input).
    const mapped = m[foldTight(raw)];
    const label = mapped || raw;
    const tokens = fuzzyTokens(label);
    for (const l of labels) {
      if (tokenSetsEqual(labelTokens.get(l), tokens)) {
        return labelCanonical.get(l);
      }
    }
    return label;
  };
}

/**
 * matchSpecificity(investor, foldedNames) -> length of the longest folded
 * name in `foldedNames` that individually dual-fold matches `investor`, or
 * null if none do. Used to break ties when an investor bidirectionally
 * substring-matches member names in MORE THAN ONE group (the same loose
 * matching that lets "Daniel" match "daniel basch" also lets it match
 * across groups) — the longer/more specific matching name is the better
 * signal of the "real" identity, same heuristic buildMemberDisplayMap uses.
 */
function matchSpecificity(investor, foldedNames) {
  let best = null;
  for (const n of foldedNames || []) {
    if (investorMatchesFoldedNames(investor, [n])) {
      const len = Math.max(n.loose ? n.loose.length : 0, n.tight ? n.tight.length : 0);
      if (best === null || len > best) best = len;
    }
  }
  return best;
}

/**
 * buildGroupResolver(groups) -> (investor) => group.name | ""
 *
 * Precomputes foldGroupMemberNames per group ONCE (not per call), then
 * returns a resolver matching a slug investor against every group's
 * bidirectional dual-fold member names — same semantics the group FILTER
 * uses. An investor can loosely match more than one group (e.g. group A's
 * member "Daniel" substring-matches "daniel basch", and so does group B's
 * fuller member "Daniel Basch"); picking array-order-first in that case can
 * disagree with which group a targeted filter puts the investor into. To
 * stay consistent, the group whose matching member name is MOST SPECIFIC
 * (longest folded match) wins across ALL groups; ties keep array order.
 * No match anywhere -> "".
 */
export function buildGroupResolver(groups) {
  const precomputed = (groups || []).map((g) => ({
    name: g.name,
    folded: foldGroupMemberNames(g),
  }));

  return (investor) => {
    let bestName = "";
    let bestSpecificity = -1;
    for (const g of precomputed) {
      const specificity = matchSpecificity(investor, g.folded);
      if (specificity !== null && specificity > bestSpecificity) {
        bestSpecificity = specificity;
        bestName = g.name;
      }
    }
    return bestName;
  };
}

/**
 * uniqueFoldedEntries(lines, field) -> [{key: foldTight(raw), raw}, ...]
 *
 * One entry per distinct foldTight(line[field]), in order of first
 * appearance; the raw value kept is the FIRST line's raw string for that
 * key (fed to displayName by the caller). Entries whose fold is "" are
 * dropped — there is no meaningful identity to offer as a dropdown option.
 */
function uniqueFoldedEntries(lines, field) {
  const seen = new Map();
  for (const line of lines || []) {
    const raw = line && line[field];
    const key = foldTight(raw);
    if (!key) continue;
    if (!seen.has(key)) seen.set(key, raw);
  }
  return [...seen.entries()].map(([key, raw]) => ({ key, raw }));
}

/**
 * rawForFoldKey(lines, field, key) -> the first raw line[field] value from
 * the FULL unfiltered `lines` (not a narrowed facet base) whose
 * foldTight(...) equals `key`, or null if no line carries it.
 *
 * Used by buildLinesFacets' selected-value retention fallback: the
 * retained value is a bare foldTight KEY (no spaces/punctuation — it came
 * from a prior selection), not a raw string, so feeding it straight into
 * displayName's raw-fallback would titleCaseDisplay a single run-together
 * token ("legacyfundii" -> "Legacyfundii") whenever the display map also
 * lacks the key. Recovering a real raw string from the wider unfiltered
 * line set (the fund/investor's OWN lines may simply be excluded from this
 * axis's narrowed facet base by a different active filter, while still
 * present elsewhere in `lines`) lets displayName recase it properly.
 */
function rawForFoldKey(lines, field, key) {
  for (const line of lines || []) {
    if (foldTight(line && line[field]) === key) return line[field];
  }
  return null;
}

/**
 * withSelectedRetained(options, selectedValue, labelFor) -> options
 *
 * If `selectedValue` is set and not already one of `options`' values,
 * appends {value: selectedValue, label: labelFor(selectedValue)} so the
 * active selection is always choosable/clearable even after the facet base
 * (computed from the OTHER active filters) no longer contains it.
 */
function withSelectedRetained(options, selectedValue, labelFor) {
  if (!selectedValue) return options;
  if (options.some((o) => o.value === selectedValue)) return options;
  return [...options, { value: selectedValue, label: labelFor(selectedValue) }];
}

/**
 * buildLinesFacets(allLines, filters, { groups, fundMap, memberMap }) ->
 * { portals:[{value,label}], funds:[{value,label}], investors:[{value,label}], groupIds:[group_id,...] }
 *
 * Cascading (faceted) dropdown options for the Lines tab: each axis's option
 * list is computed from the lines surviving every OTHER active filter (that
 * axis itself is blanked before filtering), so picking Fund X narrows the
 * Member list to members with lines in Fund X, and symmetrically for every
 * axis including Group. search/type participate in every axis's facet base
 * (they aren't blanked) — a search term or type toggle narrows all
 * dropdowns too. fundDisplayNames is deliberately NOT passed to
 * applyLinesFilters here ({} — search stays on the raw carried fields only,
 * same scope the axis lists themselves are built from).
 *
 * funds/investors consolidate to ONE entry per foldTight-canonical identity
 * (doctrine: fold-equality only, never cross-portal fuzzy identity merging)
 * — value is the canonical foldTight key, label is displayName() of the
 * first-encountered raw value for that key, Title-Cased. portals list raw
 * values as-is (portal ids aren't a fold-canonicalized identity). groupIds
 * is the list of group_id whose members dual-fold match at least one
 * facet-base line (foldGroupMemberNames + investorMatchesFoldedNames,
 * reused — not reimplemented).
 *
 * Every axis keeps its OWN current selection in its list even if the
 * faceted set no longer contains it (withSelectedRetained), so the client
 * can always see what's selected and change or clear it. Portal retention
 * keeps the raw value verbatim (label=value, same as the base portal list —
 * portal ids get no case transform anywhere). Fund/investor retention
 * resolves a real raw string for the retained foldTight key from the FULL
 * unfiltered line set (rawForFoldKey), not just the axis's own narrowed
 * facet base, before formatting via displayName — so the label stays a
 * readable name even when the selected identity's own lines are excluded
 * from its facet base by a different active filter.
 */
export function buildLinesFacets(allLines, filters, { groups, fundMap, memberMap } = {}) {
  const lines = allLines || [];
  const gList = groups || [];
  const fMap = fundMap || {};
  const mMap = memberMap || {};
  const f = { search: "", portal: "", fund: "", investor: "", group: "", type: "", ...filters };

  const facetBase = (axis) => applyLinesFilters(lines, { ...f, [axis]: "" }, gList, {});

  const portalValues = [...new Set(facetBase("portal").map((l) => l.portal).filter(Boolean))].sort();
  let portals = portalValues.map((v) => ({ value: v, label: v }));
  portals = withSelectedRetained(portals, f.portal, (v) => v);

  let funds = uniqueFoldedEntries(facetBase("fund"), "fund")
    .map(({ key, raw }) => ({ value: key, label: displayName(raw, fMap) }))
    .sort((a, b) => a.label.localeCompare(b.label));
  funds = withSelectedRetained(funds, f.fund, (v) => displayName(rawForFoldKey(lines, "fund", v) ?? v, fMap));

  let investors = uniqueFoldedEntries(facetBase("investor"), "investor")
    .map(({ key, raw }) => ({ value: key, label: displayName(raw, mMap) }))
    .sort((a, b) => a.label.localeCompare(b.label));
  investors = withSelectedRetained(
    investors,
    f.investor,
    (v) => displayName(rawForFoldKey(lines, "investor", v) ?? v, mMap)
  );

  const groupBase = facetBase("group");
  let groupIds = gList
    .filter((g) => {
      const folded = foldGroupMemberNames(g);
      return groupBase.some((line) => investorMatchesFoldedNames(line.investor, folded));
    })
    .map((g) => g.group_id);
  if (f.group && !groupIds.includes(f.group)) groupIds = [...groupIds, f.group];

  return { portals, funds, investors, groupIds };
}

/**
 * groupLinesByMember(lines, memberMap) -> [{key, label, lines:[...]}, ...]
 *
 * Sections are keyed by foldTight(line.investor), so spelling variants that
 * fold identically collapse into one section. Section order = order of
 * first appearance in the input (input is assumed already sorted); lines
 * within a section keep their input order. Lines whose investor folds to ""
 * are pulled out into a single section labeled "—", appended LAST
 * regardless of where such lines first appeared.
 */
export function groupLinesByMember(lines, memberMap) {
  const sections = [];
  const indexByKey = new Map();
  const emptySection = { key: "", label: "—", lines: [] };
  let hasEmpty = false;

  for (const line of lines || []) {
    const investor = line.investor;
    const key = foldTight(investor);

    if (!key) {
      emptySection.lines.push(line);
      hasEmpty = true;
      continue;
    }

    let idx = indexByKey.get(key);
    if (idx === undefined) {
      idx = sections.length;
      indexByKey.set(key, idx);
      sections.push({ key, label: displayName(investor, memberMap), lines: [] });
    }
    sections[idx].lines.push(line);
  }

  if (hasEmpty) sections.push(emptySection);
  return sections;
}

/**
 * commitmentRows(portals) -> [{portal, fund, investor, amount, currency}, ...]
 *
 * One row per overview member whose commitment_total is a real number
 * (skips null/undefined). fund/investor stay as SLUGS here — the CSV
 * builder is responsible for turning them into display strings. currency
 * falls back member -> fund -> "USD".
 */
export function commitmentRows(portals) {
  const rows = [];
  for (const portal of portals || []) {
    for (const fund of (portal && portal.funds) || []) {
      for (const member of (fund && fund.members) || []) {
        if (member.commitment_total == null) continue;
        rows.push({
          portal: portal.portal,
          fund: fund.fund_name,
          investor: member.investor_name,
          amount: member.commitment_total,
          currency: member.currency || fund.currency || "USD",
        });
      }
    }
  }
  return rows;
}

// Same safe-currency-code normalization LinesTable.jsx uses — any 3-letter
// alpha code passes through uppercased, anything else falls back to USD.
function safeCurrencyCode(currency) {
  const normalized = String(currency || "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(normalized) ? normalized : "USD";
}

const CSV_TYPE_LABEL = {
  capital_call_event_amount: "Call",
  distribution_event_amount: "Distribution",
};
const CSV_TYPE_SIGN = {
  capital_call_event_amount: -1,
  distribution_event_amount: 1,
};

/**
 * RFC 4180 field escaping: quote if it contains a comma, quote, CR or LF; double inner quotes.
 *
 * CSV formula-injection neutralization (CWE-1236 / OWASP CSV Injection guidance): a
 * STRING field that starts with =, +, -, @, tab, or CR is a formula trigger for
 * Excel/Sheets when the file is later opened, so it gets a leading single quote to
 * force plain-text interpretation. Real NUMBER values (only the Amount column) are
 * exempt and must stay raw/unquoted/unprefixed — a JS number can never be parsed as
 * a spreadsheet formula, and a negative call amount must survive as a bare negative
 * number for Excel to treat it as numeric.
 */
function csvField(value) {
  if (typeof value === "number") return String(value);
  const s = value == null ? "" : String(value);
  const guarded = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  if (/[",\r\n]/.test(guarded)) {
    return `"${guarded.replace(/"/g, '""')}"`;
  }
  return guarded;
}

function csvRow(fields) {
  return fields.map(csvField).join(",");
}

function documentLabel(line) {
  if (line.original_filename) return line.original_filename;
  if (line.source_doc_id) {
    const parts = String(line.source_doc_id).split("/");
    return parts[parts.length - 1] || "";
  }
  return "";
}

/**
 * lineDocumentName(line, { memberMap, resolveGroup }) -> patterned document name.
 *
 * Pattern: "Group_Member_Type_Date" (underscore-joined), e.g.
 * "DRB_Daniel Basch_Call_2026-07-15". No group on the line -> that segment
 * is simply dropped, not left as an empty gap: "Lior Pozin_Call_2026-07-15".
 * Segments:
 *   - resolveGroup(line.investor) (group name, "" if none)
 *   - displayName(line.investor, memberMap) (Title Case member name)
 *   - typeLabel: "Call"/"Distribution" for the two known backend types,
 *     else the raw type string verbatim
 *   - (line.event_date || "").slice(0, 10)
 * Non-empty segments are joined with "_". If EVERY segment is empty (no
 * investor, no type, no date, no resolvable group — nothing to build a name
 * from), falls back to the SOURCE document name: original_filename, else
 * the last path segment of source_doc_id, else "Unknown".
 */
export function lineDocumentName(line, { memberMap, resolveGroup } = {}) {
  const resolveGroupFn = resolveGroup || (() => "");
  const mMap = memberMap || {};

  const knownType = Object.prototype.hasOwnProperty.call(CSV_TYPE_LABEL, line.type);
  const typeLabel = knownType ? CSV_TYPE_LABEL[line.type] : String(line.type || "");

  const segments = [
    resolveGroupFn(line.investor) || "",
    displayName(line.investor, mMap),
    typeLabel,
    line.event_date ? String(line.event_date).slice(0, 10) : "",
  ].filter((seg) => seg !== "");

  if (segments.length === 0) return documentLabel(line) || "Unknown";
  return segments.join("_");
}

function tagsLabel(line) {
  const tags = [];
  if (line.discarded) tags.push("discarded");
  if (line.excluded) tags.push("excluded");
  return tags.join(" ");
}

/**
 * buildLinesCsv({ lines, commitments, fundMap, memberMap, resolveGroup }) -> CSV string (no BOM)
 *
 * Header: Portal,Fund,Member,Group,Date,Type,Amount,Currency,Document,Tags
 *
 * Event rows (`lines`, in the order given): Fund/Member rendered via
 * displayName (Title Case); Group via resolveGroup(line.investor); Date =
 * event_date (YYYY-MM-DD) or ""; Type = "Call"/"Distribution" for the two
 * known backend types, else the raw type string verbatim; Amount = signed
 * plain number (calls negative, distributions positive, unknown types
 * unsigned as-is) — no currency symbol, no thousands separator; Currency =
 * safe 3-letter code; Document = lineDocumentName(line, {memberMap,
 * resolveGroup}) — the patterned "Group_Member_Type_Date" name, falling
 * back to the source filename only when every pattern segment is empty;
 * Tags = "discarded"/"excluded"/"discarded excluded"/"".
 *
 * Commitment rows (`commitments`) are appended AFTER all event rows, same
 * columns: Type="Commitment (total)", Date/Document/Tags="", Amount unsigned
 * positive.
 *
 * Caller prepends the UTF-8 BOM ("﻿") before writing to a Blob — this
 * function returns the raw string.
 */
export function buildLinesCsv({ lines, commitments, fundMap, memberMap, resolveGroup }) {
  const resolveGroupFn = resolveGroup || (() => "");
  const fMap = fundMap || {};
  const mMap = memberMap || {};

  const rows = ["Portal,Fund,Member,Group,Date,Type,Amount,Currency,Document,Tags"];

  for (const line of lines || []) {
    const knownType = Object.prototype.hasOwnProperty.call(CSV_TYPE_LABEL, line.type);
    const typeLabel = knownType ? CSV_TYPE_LABEL[line.type] : String(line.type || "");
    const sign = knownType ? CSV_TYPE_SIGN[line.type] : 1;
    const amount = line.amount == null ? "" : Math.abs(Number(line.amount)) * sign;

    rows.push(
      csvRow([
        line.portal || "",
        displayName(line.fund, fMap),
        displayName(line.investor, mMap),
        resolveGroupFn(line.investor) || "",
        line.event_date ? String(line.event_date).slice(0, 10) : "",
        typeLabel,
        amount,
        safeCurrencyCode(line.currency),
        lineDocumentName(line, { memberMap: mMap, resolveGroup: resolveGroupFn }),
        tagsLabel(line),
      ])
    );
  }

  for (const c of commitments || []) {
    const amount = c.amount == null ? "" : Math.abs(Number(c.amount));
    rows.push(
      csvRow([
        c.portal || "",
        displayName(c.fund, fMap),
        displayName(c.investor, mMap),
        resolveGroupFn(c.investor) || "",
        "",
        "Commitment (total)",
        amount,
        safeCurrencyCode(c.currency),
        "",
        "",
      ])
    );
  }

  return rows.join("\r\n");
}
